var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useVerifyVoiceProviderLeg } from './voice-admin-api';
import { useToast } from '@/hoc';
import { useConfigContext } from '@/hoc/context/config-provider';
import { float32ToPcm16ArrayBuffer, pcm16Base64ToAudioBuffer, resampleMonoFloat32, } from '@/context/realtime/audio-utils';
import { CheckCircle2, Loader2, Mic, Play, RefreshCw, Square, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui';
const STATUS_BADGE = {
    verified: 'bg-dash-success/15 text-dash-successInk',
    unverified: 'bg-dash-surface text-dash-muted',
    failed: 'bg-dash-danger/15 text-dash-dangerInk',
};
const DEFAULT_TTS_PHRASE = 'Hello, this is a voice provider connectivity test.';
function getAudioContext() {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    return new Ctor();
}
/** STT input sample rate the provider declares (input_format, then azure header), default 16k. */
function sttInputRate(provider) {
    var _a, _b, _c;
    const spec = provider.stt || {};
    const fromInput = (_a = spec.input_format) === null || _a === void 0 ? void 0 : _a.sample_rate;
    const fromAzure = (_c = ((_b = spec.azure) === null || _b === void 0 ? void 0 : _b.first_chunk_wav_header)) === null || _c === void 0 ? void 0 : _c.sample_rate;
    return Number(fromInput || fromAzure || 16000) || 16000;
}
/** Build the test WebSocket URL from the interview WS base in config. */
function testWsUrl(webSocketUrl, providerId) {
    const base = (webSocketUrl || '').replace(/ws\/interview\/?$/, 'ws/voice-provider-test/');
    return `${base}${providerId}/`;
}
const VoiceProviderTestPanel = ({ provider }) => {
    var _a, _b;
    const { showToast } = useToast();
    const { config } = useConfigContext();
    const verifyLeg = useVerifyVoiceProviderLeg();
    const [status, setStatus] = useState(provider.verification_status || 'unverified');
    const [warm, setWarm] = useState('connecting');
    const [warmError, setWarmError] = useState('');
    const [ttsState, setTtsState] = useState({ status: 'idle' });
    const [ttsText, setTtsText] = useState(DEFAULT_TTS_PHRASE);
    const [sttState, setSttState] = useState({ status: 'idle' });
    const [sttFinal, setSttFinal] = useState('');
    const [sttPartial, setSttPartial] = useState('');
    const [recording, setRecording] = useState(false);
    // Shared warm socket + incremental TTS playback (play each frame as it arrives — like the
    // interview's playBotQueue — so audio starts on the first chunk, not after the whole utterance).
    const wsRef = useRef(null);
    const ttsRateRef = useRef(24000);
    const ttsCtxRef = useRef(null);
    const ttsNextTimeRef = useRef(0);
    const ttsChunkCountRef = useRef(0);
    // Some providers (e.g. Sarvam) return COMPRESSED audio (MP3), not raw PCM. Those can't be
    // scheduled per-frame as PCM (that's just noise) — we buffer them and decode once via
    // decodeAudioData. Detected from the first frame's magic bytes.
    const ttsCompressedRef = useRef(null);
    const ttsCompressedChunksRef = useRef([]);
    // Mic capture refs.
    const streamRef = useRef(null);
    const ctxRef = useRef(null);
    const procRef = useRef(null);
    const sourceRef = useRef(null);
    // Operator-selectable language + voice for the TTS test (default to the
    // provider's defaults). Voices are filtered to the chosen language.
    const langs = provider.languages || [];
    const [selLang, setSelLang] = useState(provider.default_language || ((_a = langs[0]) === null || _a === void 0 ? void 0 : _a.code) || '');
    const [selVoice, setSelVoice] = useState(provider.default_voice || ((_b = (provider.voices || [])[0]) === null || _b === void 0 ? void 0 : _b.voice_id) || '');
    const voiceOpts = (provider.voices || []).filter((v) => !selLang || !v.language || v.language === selLang);
    const onLangChange = (code) => {
        setSelLang(code);
        const first = (provider.voices || []).find((v) => !v.language || v.language === code);
        if (first)
            setSelVoice(first.voice_id);
    };
    const language = selLang || undefined;
    const voice = selVoice || undefined;
    // ── incremental TTS playback ────────────────────────────────────────────────────────────
    const ensureTtsCtx = () => {
        let ctx = ttsCtxRef.current;
        if (!ctx || ctx.state === 'closed') {
            ctx = getAudioContext();
            ttsCtxRef.current = ctx;
        }
        if (ctx.state === 'suspended')
            ctx.resume().catch(() => { });
        return ctx;
    };
    /** Reset the playback cursor + buffers before a new synthesis turn. */
    const resetTtsPlayback = () => {
        ttsNextTimeRef.current = 0;
        ttsChunkCountRef.current = 0;
        ttsCompressedRef.current = null;
        ttsCompressedChunksRef.current = [];
    };
    const b64ToBytes = (b64) => {
        const bin = atob(b64);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i);
        return out;
    };
    /** True if the bytes look like a compressed container we must decodeAudioData (MP3/OGG),
     *  rather than raw PCM we can schedule directly. */
    const looksCompressed = (b) => {
        if (b.length < 2)
            return false;
        if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33)
            return true; // "ID3" (MP3 w/ tag)
        if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0)
            return true; // MP3 frame sync 0xFFEx
        if (b[0] === 0x4f && b[1] === 0x67 && b[2] === 0x67)
            return true; // "Ogg"
        return false;
    };
    /** Concatenate buffered compressed frames and play via decodeAudioData (handles MP3). */
    const playCompressedBuffered = () => __awaiter(void 0, void 0, void 0, function* () {
        const chunks = ttsCompressedChunksRef.current;
        const total = chunks.reduce((n, c) => n + c.length, 0);
        const merged = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) {
            merged.set(c, off);
            off += c.length;
        }
        const ctx = ensureTtsCtx();
        const audioBuf = yield ctx.decodeAudioData(merged.buffer.slice(0));
        const src = ctx.createBufferSource();
        src.buffer = audioBuf;
        src.connect(ctx.destination);
        src.start();
    });
    /** Schedule ONE PCM frame the instant it arrives, back-to-back on a running cursor. */
    const playTtsFrame = (b64, sampleRate) => {
        const ctx = ensureTtsCtx();
        const buffer = pcm16Base64ToAudioBuffer(b64, ctx, sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);
        const startAt = Math.max(ttsNextTimeRef.current, ctx.currentTime + 0.05);
        src.start(startAt);
        ttsNextTimeRef.current = startAt + buffer.duration;
    };
    const stopMic = () => {
        var _a, _b, _c, _d, _e;
        try {
            (_a = procRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            (_b = sourceRef.current) === null || _b === void 0 ? void 0 : _b.disconnect();
            (_c = streamRef.current) === null || _c === void 0 ? void 0 : _c.getTracks().forEach((t) => t.stop());
            (_e = (_d = ctxRef.current) === null || _d === void 0 ? void 0 : _d.close) === null || _e === void 0 ? void 0 : _e.call(_d);
        }
        catch (_f) {
            /* best effort */
        }
        procRef.current = null;
        sourceRef.current = null;
        streamRef.current = null;
        ctxRef.current = null;
    };
    // ── warm socket: open on mount, close on unmount ───────────────────────────────────────
    const openWarmSocket = () => {
        setWarm('connecting');
        setWarmError('');
        let ws;
        try {
            ws = new WebSocket(testWsUrl(config.webSocketUrl, provider.provider_id));
        }
        catch (_a) {
            setWarm('error');
            setWarmError('could not open test connection');
            return;
        }
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;
        ws.onmessage = (evt) => {
            var _a;
            let msg;
            try {
                msg = JSON.parse(evt.data);
            }
            catch (_b) {
                return;
            }
            switch (msg.type) {
                case 'ready':
                    setWarm('ready');
                    break;
                case 'error':
                    setWarm('error');
                    setWarmError(msg.error || 'could not connect to the provider');
                    break;
                case 'tts_audio':
                    if (msg.sample_rate)
                        ttsRateRef.current = msg.sample_rate;
                    if (msg.audio) {
                        try {
                            const bytes = b64ToBytes(msg.audio);
                            // Decide PCM vs compressed from the very first frame.
                            if (ttsCompressedRef.current === null) {
                                ttsCompressedRef.current = looksCompressed(bytes);
                            }
                            ttsChunkCountRef.current += 1;
                            if (ttsCompressedRef.current) {
                                // MP3/OGG: buffer now, decode+play on tts_done.
                                ttsCompressedChunksRef.current.push(bytes);
                                if (ttsChunkCountRef.current === 1) {
                                    setTtsState({ status: 'busy', message: 'Buffering audio…' });
                                }
                            }
                            else {
                                // Raw PCM: schedule immediately for low-latency playback.
                                playTtsFrame(msg.audio, ttsRateRef.current);
                                if (ttsChunkCountRef.current === 1) {
                                    setTtsState({ status: 'busy', message: 'Playing…' });
                                }
                            }
                        }
                        catch (_c) {
                            setTtsState({ status: 'fail', message: 'could not play returned audio' });
                        }
                    }
                    break;
                case 'tts_done': {
                    const n = (_a = msg.chunks) !== null && _a !== void 0 ? _a : ttsChunkCountRef.current;
                    if (ttsCompressedRef.current && ttsCompressedChunksRef.current.length) {
                        // Compressed (MP3/OGG): decode the whole buffer now, then play.
                        playCompressedBuffered()
                            .then(() => setTtsState((prev) => prev.status === 'fail'
                            ? prev
                            : { status: 'pass', message: `Playing ${n} chunk(s), decoded MP3 — does it sound right?` }))
                            .catch(() => setTtsState({ status: 'fail', message: 'could not decode returned audio (MP3?)' }));
                    }
                    else {
                        setTtsState((prev) => prev.status === 'fail'
                            ? prev
                            : { status: 'pass', message: `Played ${n} audio chunk(s) — does it sound right?` });
                    }
                    break;
                }
                case 'tts_error':
                    setTtsState({ status: 'fail', message: msg.error || 'TTS failed' });
                    break;
                case 'partial':
                    setSttPartial(msg.text || '');
                    break;
                case 'final':
                    setSttPartial('');
                    setSttFinal((prev) => (prev ? `${prev} ${msg.text || ''}` : msg.text || ''));
                    break;
                default:
                    break;
            }
        };
        ws.onerror = () => {
            if (warm !== 'ready') {
                setWarm('error');
                setWarmError('connection error');
            }
        };
        ws.onclose = () => {
            setRecording(false);
        };
    };
    useEffect(() => {
        openWarmSocket();
        return () => {
            var _a, _b;
            stopMic();
            try {
                (_b = (_a = ttsCtxRef.current) === null || _a === void 0 ? void 0 : _a.close) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            catch (_c) {
                /* ignore */
            }
            ttsCtxRef.current = null;
            if (wsRef.current && wsRef.current.readyState <= 1) {
                try {
                    wsRef.current.close();
                }
                catch (_d) {
                    /* ignore */
                }
            }
            wsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const socketReady = warm === 'ready';
    // ── TTS (over the warm socket) ──────────────────────────────────────────────────────────
    const runTtsTest = () => {
        const phrase = ttsText.trim();
        if (!phrase) {
            setTtsState({ status: 'fail', message: 'enter some text to synthesize' });
            return;
        }
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !socketReady)
            return;
        resetTtsPlayback();
        ensureTtsCtx(); // create/resume the context inside the click gesture (autoplay policy)
        setTtsState({ status: 'busy', message: 'Synthesizing…' });
        ws.send(JSON.stringify({ type: 'tts', phrase, language, voice }));
    };
    // ── STT (stream over the warm socket) ───────────────────────────────────────────────────
    const startLiveStt = () => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN || !socketReady)
            return;
        setSttFinal('');
        setSttPartial('');
        navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
            const ctx = getAudioContext();
            const source = ctx.createMediaStreamSource(stream);
            const proc = ctx.createScriptProcessor(4096, 1, 1);
            const targetRate = sttInputRate(provider);
            proc.onaudioprocess = (e) => {
                const sock = wsRef.current;
                if (!sock || sock.readyState !== WebSocket.OPEN)
                    return;
                const input = new Float32Array(e.inputBuffer.getChannelData(0));
                const resampled = resampleMonoFloat32(input, ctx.sampleRate, targetRate);
                sock.send(float32ToPcm16ArrayBuffer(resampled));
            };
            source.connect(proc);
            proc.connect(ctx.destination);
            streamRef.current = stream;
            ctxRef.current = ctx;
            procRef.current = proc;
            sourceRef.current = source;
            setRecording(true);
            setSttState({ status: 'busy', message: 'Listening — speak a sentence, then Stop.' });
        })
            .catch(() => {
            setSttState({ status: 'fail', message: 'microphone permission denied or unavailable' });
            stopMic();
        });
    };
    const stopLiveStt = () => {
        stopMic();
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            try {
                ws.send(JSON.stringify({ type: 'stt_stop' }));
            }
            catch (_a) {
                /* ignore */
            }
        }
        setRecording(false);
        setTimeout(() => {
            setSttState((prev) => prev.status === 'fail'
                ? prev
                : { status: 'pass', message: 'Transcript received — does it match what you said?' });
        }, 800);
    };
    // ── verify ─────────────────────────────────────────────────────────────────────────────
    const confirmLeg = (leg, ok, error) => {
        verifyLeg.mutate({ providerId: provider.provider_id, leg, ok, error }, {
            onSuccess: (updated) => {
                setStatus(updated.verification_status || 'unverified');
                showToast({
                    message: ok ? `${leg.toUpperCase()} marked verified` : `${leg.toUpperCase()} marked failed`,
                    status: ok ? 'success' : 'info',
                });
            },
            onError: (e) => showToast({ message: (e === null || e === void 0 ? void 0 : e.message) || 'Verify failed', status: 'error' }),
        });
    };
    const legIcon = (s) => {
        if (s.status === 'busy')
            return _jsx(Loader2, { className: 'h-4 w-4 animate-spin' });
        if (s.status === 'pass')
            return _jsx(CheckCircle2, { className: 'h-4 w-4 text-dash-successInk' });
        if (s.status === 'fail')
            return _jsx(XCircle, { className: 'h-4 w-4 text-dash-dangerInk' });
        return null;
    };
    const ttsVerified = Boolean(provider.tts_verified_at);
    const sttVerified = Boolean(provider.stt_verified_at);
    const liveTranscript = `${sttFinal}${sttFinal && sttPartial ? ' ' : ''}${sttPartial}`.trim();
    const canVerifyStt = sttState.status === 'pass' && !!liveTranscript;
    return (_jsxs("div", { className: 'space-y-5', children: [_jsxs("div", { className: 'flex items-center justify-between', children: [_jsx("p", { className: 'text-sm text-dash-muted', children: "Verify both legs against the live vendor before this provider can be used in interviews." }), _jsx("span", { className: `rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_BADGE[status]}`, children: status })] }), warm === 'connecting' && (_jsxs("p", { className: 'flex items-center gap-2 rounded-md bg-dash-surface px-3 py-2 text-sm text-dash-muted', children: [_jsx(Loader2, { className: 'h-4 w-4 animate-spin' }), " Connecting to the provider\u2026 tests will be instant once ready."] })), warm === 'error' && (_jsxs("div", { className: 'flex items-center justify-between rounded-md bg-dash-danger/10 px-3 py-2 text-sm text-dash-dangerInk', children: [_jsxs("span", { className: 'flex items-center gap-2', children: [_jsx(XCircle, { className: 'h-4 w-4' }), " ", warmError || 'could not connect'] }), _jsx(Button, { variant: 'outline', size: 'sm', onClick: openWarmSocket, rightIcon: _jsx(RefreshCw, { className: 'h-4 w-4' }), children: "Reconnect" })] })), warm === 'ready' && (_jsxs("p", { className: 'flex items-center gap-2 text-sm text-dash-successInk', children: [_jsx(CheckCircle2, { className: 'h-4 w-4' }), " Connected \u2014 tests run instantly."] })), _jsxs("div", { className: 'rounded-xl border border-dash-border p-4', children: [_jsxs("div", { className: 'mb-2 flex items-center justify-between', children: [_jsxs("p", { className: 'text-sm font-semibold text-dash-text', children: ["Text-to-Speech ", ttsVerified && _jsx(CheckCircle2, { className: 'inline h-4 w-4 text-dash-successInk' })] }), _jsx(Button, { variant: 'outline', size: 'sm', onClick: runTtsTest, disabled: !socketReady, isLoading: ttsState.status === 'busy', rightIcon: _jsx(Play, { className: 'h-4 w-4' }), children: "Test TTS" })] }), (langs.length > 0 || voiceOpts.length > 0) && (_jsxs("div", { className: 'mb-3 grid grid-cols-2 gap-2', children: [_jsxs("div", { children: [_jsx("label", { className: 'mb-1 block text-xs font-medium text-dash-muted', children: "Language" }), _jsx("select", { className: 'w-full rounded-md border border-dash-border bg-dash-surface px-2 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent', value: selLang, onChange: (e) => onLangChange(e.target.value), children: langs.map((l) => (_jsx("option", { value: l.code, children: l.label || l.code }, l.code))) })] }), _jsxs("div", { children: [_jsx("label", { className: 'mb-1 block text-xs font-medium text-dash-muted', children: "Voice" }), _jsx("select", { className: 'w-full rounded-md border border-dash-border bg-dash-surface px-2 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent', value: selVoice, onChange: (e) => setSelVoice(e.target.value), children: voiceOpts.map((v) => (_jsxs("option", { value: v.voice_id, children: [v.label || v.voice_id, v.gender ? ` (${v.gender})` : ''] }, v.voice_id))) })] })] })), _jsx("label", { className: 'mb-1 block text-xs font-medium text-dash-muted', children: "Text to synthesize" }), _jsx("textarea", { className: 'flex min-h-[60px] w-full rounded-md border border-dash-border bg-dash-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent', value: ttsText, onChange: (e) => setTtsText(e.target.value), placeholder: 'Type any text to hear this provider speak it\u2026' }), ttsState.status !== 'idle' && (_jsxs("div", { className: 'mt-2 flex items-center gap-2 text-sm text-dash-muted', children: [legIcon(ttsState), " ", _jsx("span", { children: ttsState.message })] })), ttsState.status === 'pass' && (_jsxs("div", { className: 'mt-3 flex gap-2', children: [_jsx(Button, { variant: 'primary', size: 'sm', onClick: () => confirmLeg('tts', true), children: "Sounds correct \u2713" }), _jsx(Button, { variant: 'dangerSecondary', size: 'sm', onClick: () => confirmLeg('tts', false, 'operator rejected TTS output'), children: "Not right" })] }))] }), _jsxs("div", { className: 'rounded-xl border border-dash-border p-4', children: [_jsxs("div", { className: 'mb-2 flex items-center justify-between', children: [_jsxs("p", { className: 'text-sm font-semibold text-dash-text', children: ["Speech-to-Text ", sttVerified && _jsx(CheckCircle2, { className: 'inline h-4 w-4 text-dash-successInk' })] }), recording ? (_jsx(Button, { variant: 'dangerSecondary', size: 'sm', onClick: stopLiveStt, rightIcon: _jsx(Square, { className: 'h-4 w-4' }), children: "Stop" })) : (_jsx(Button, { variant: 'outline', size: 'sm', onClick: startLiveStt, disabled: !socketReady, rightIcon: _jsx(Mic, { className: 'h-4 w-4' }), children: "Start live STT" }))] }), sttState.status !== 'idle' && (_jsxs("div", { className: 'flex items-center gap-2 text-sm text-dash-muted', children: [legIcon(sttState), " ", _jsx("span", { children: sttState.message })] })), (liveTranscript || recording) && (_jsx("p", { className: 'mt-2 min-h-[2.5rem] rounded-md bg-dash-surface px-3 py-2 text-sm text-dash-text', children: liveTranscript ? (_jsxs(_Fragment, { children: [sttFinal, sttFinal && sttPartial ? ' ' : '', _jsx("span", { className: 'text-dash-muted', children: sttPartial })] })) : (_jsx("span", { className: 'text-dash-muted', children: "Listening\u2026" })) })), canVerifyStt && (_jsxs("div", { className: 'mt-3 flex gap-2', children: [_jsx(Button, { variant: 'primary', size: 'sm', onClick: () => confirmLeg('stt', true), children: "Transcript correct \u2713" }), _jsx(Button, { variant: 'dangerSecondary', size: 'sm', onClick: () => confirmLeg('stt', false, 'operator rejected STT transcript'), children: "Not right" })] }))] }), status === 'verified' && (_jsxs("p", { className: 'flex items-center gap-2 text-sm text-dash-successInk', children: [_jsx(CheckCircle2, { className: 'h-4 w-4' }), " Both legs verified \u2014 this provider can now be selected for interviews."] }))] }));
};
export default VoiceProviderTestPanel;
